import React, { useState, useEffect } from 'react';
import { toast } from './Toast';
import DOMPurify from 'dompurify';
import { Mail, Eye, Send, FileText, X, Pencil, Zap, ChevronRight } from 'lucide-react';
import { crmApi } from '../services/crmApi';

interface EmailTemplate {
  id: string;
  product_code: string | null;
  category: string;
  name: string;
  subject: string;
  body: string;
}

interface EmailPreview {
  subject: string;
  body: string;
  html: string;
}

interface EmailTemplateSelectorProps {
  applicationId: number;
  serviceType?: string | null;
  onClose?: () => void;
  onSend?: (templateId: string, preview: EmailPreview) => void;
}

const SERVICE_TO_PRODUCT: Record<string, string> = {
  pup_grant: 'pup_grant',
  dotacja_pup: 'pup_grant',
  dotacja_pup_on: 'pup_grant',
  szkolenie_kfs: 'kfs',
  kfs: 'kfs',
  wsparcie_na_starcie: 'wsparcie_na_starcie',
  pozyczka_rozwojowa: 'pozyczki_rozwojowe',
  pozyczki_rozwojowe: 'pozyczki_rozwojowe',
  bhp: 'bhp',
  zus: 'zus',
};

const PRODUCT_LABELS: Record<string, string> = {
  pup_grant: 'Dotacje PUP',
  kfs: 'KFS Szkolenia',
  wsparcie_na_starcie: 'Pozyczki WNS',
  pozyczki_rozwojowe: 'Pozyczka Rozwojowa',
  bhp: 'BHP',
  zus: 'ZUS',
};

const CATEGORY_LABELS: Record<string, string> = {
  all: 'Wszystkie',
  onboarding: 'Powitanie',
  verification: 'Weryfikacja',
  alerts: 'Ostrzezenia',
  process: 'Proces',
  outcome: 'Wynik',
  reporting: 'Sprawozdania',
  communication: 'Komunikacja',
};

export const EmailTemplateSelector: React.FC<EmailTemplateSelectorProps> = ({
  applicationId,
  serviceType,
  onClose,
  onSend,
}) => {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [preview, setPreview] = useState<EmailPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [showAllProducts, setShowAllProducts] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editSubject, setEditSubject] = useState('');
  const [editBody, setEditBody] = useState('');
  const [sending, setSending] = useState(false);

  const productCode = serviceType ? SERVICE_TO_PRODUCT[serviceType] : null;

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const data = await crmApi.getEmailTemplates();
      setTemplates(data);
    } catch (error) {
      console.error('Error loading templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTemplate = async (templateId: string) => {
    setSelectedTemplate(templateId);
    setPreviewLoading(true);
    setEditMode(false);

    try {
      const data = await crmApi.previewEmail(applicationId, templateId);
      setPreview(data);
      setEditSubject(data.subject);
      setEditBody(data.body);
    } catch (error) {
      console.error('Error loading preview:', error);
      setPreview(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSendEmail = async () => {
    if (!selectedTemplate || !preview) return;

    const finalPreview: EmailPreview = editMode
      ? { subject: editSubject, body: editBody, html: editBody.replace(/\n/g, '<br>') }
      : preview;

    if (onSend) {
      setSending(true);
      try {
        await onSend(selectedTemplate, finalPreview);
      } finally {
        setSending(false);
      }
    } else {
      const emailText = `Temat: ${finalPreview.subject}\n\n${finalPreview.body}`;
      navigator.clipboard.writeText(emailText);
      toast.success('Skopiowano do schowka!');
    }
  };

  // Filter templates: product-matched first, then rest
  const matchedTemplates = templates.filter(t =>
    productCode ? (t.product_code === productCode || !t.product_code) : true
  );
  const otherTemplates = templates.filter(t =>
    productCode ? (t.product_code && t.product_code !== productCode) : false
  );

  const displayTemplates = showAllProducts ? templates : matchedTemplates;

  const categories = ['all', ...new Set(displayTemplates.map(t => t.category))];
  const filteredTemplates =
    selectedCategory === 'all'
      ? displayTemplates
      : displayTemplates.filter(t => t.category === selectedCategory);

  // Quick actions — onboarding templates for current product
  const quickTemplates = matchedTemplates
    .filter(t => t.product_code === productCode)
    .slice(0, 4);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-6xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-5 rounded-t-lg flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Mail className="w-7 h-7" />
            <div>
              <h2 className="text-xl font-bold">
                Email do klienta
                {productCode && (
                  <span className="ml-2 text-sm font-normal bg-white/20 px-2 py-0.5 rounded">
                    {PRODUCT_LABELS[productCode] || productCode}
                  </span>
                )}
              </h2>
              <p className="text-purple-100 text-xs mt-0.5">
                Wybierz szablon, edytuj i wyślij z adresu dopasowanego do produktu
              </p>
            </div>
          </div>
          {onClose && (
            <button onClick={onClose} className="text-white hover:bg-white/20 rounded-lg p-2">
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Quick actions bar */}
        {quickTemplates.length > 0 && (
          <div className="bg-purple-50 border-b border-purple-100 px-4 py-2.5 flex items-center gap-2 overflow-x-auto">
            <Zap size={14} className="text-purple-500 shrink-0" />
            <span className="text-xs text-purple-600 font-medium shrink-0">Szybkie:</span>
            {quickTemplates.map(t => (
              <button
                key={t.id}
                onClick={() => handleSelectTemplate(t.id)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-all whitespace-nowrap ${
                  selectedTemplate === t.id
                    ? 'bg-purple-600 text-white'
                    : 'bg-white text-purple-700 border border-purple-200 hover:bg-purple-100'
                }`}
              >
                {t.name.replace(/^\[.*?\]\s*/, '')}
              </button>
            ))}
          </div>
        )}

        <div className="flex flex-1 overflow-hidden">
          {/* Left Sidebar - Template List */}
          <div className="w-1/3 border-r border-gray-200 flex flex-col">
            {/* Filters */}
            <div className="p-3 border-b border-gray-200 space-y-2">
              <select
                value={selectedCategory}
                onChange={e => setSelectedCategory(e.target.value)}
                className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>
                    {CATEGORY_LABELS[cat] || cat}
                  </option>
                ))}
              </select>
              {productCode && otherTemplates.length > 0 && (
                <label className="flex items-center gap-2 text-xs text-gray-500 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showAllProducts}
                    onChange={e => setShowAllProducts(e.target.checked)}
                    className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                  />
                  Pokaż szablony innych produktow ({otherTemplates.length})
                </label>
              )}
            </div>

            {/* Templates List */}
            <div className="flex-1 overflow-y-auto p-3 space-y-1.5">
              {loading ? (
                <div className="text-center py-8 text-gray-500 text-sm">Ładowanie...</div>
              ) : filteredTemplates.length === 0 ? (
                <div className="text-center py-8 text-gray-500 text-sm">
                  Brak szablonów w tej kategorii
                </div>
              ) : (
                filteredTemplates.map(template => (
                  <button
                    key={template.id}
                    onClick={() => handleSelectTemplate(template.id)}
                    className={`w-full text-left p-2.5 rounded-lg border transition-all ${
                      selectedTemplate === template.id
                        ? 'border-purple-600 bg-purple-50 shadow-sm'
                        : 'border-gray-100 hover:border-purple-200 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <FileText
                        className={`w-4 h-4 mt-0.5 shrink-0 ${
                          selectedTemplate === template.id ? 'text-purple-600' : 'text-gray-400'
                        }`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {template.name}
                        </div>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <span className="text-[10px] text-gray-400">
                            {CATEGORY_LABELS[template.category] || template.category}
                          </span>
                          {template.product_code && template.product_code !== productCode && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">
                              {PRODUCT_LABELS[template.product_code] || template.product_code}
                            </span>
                          )}
                        </div>
                      </div>
                      <ChevronRight size={14} className="text-gray-300 mt-1 shrink-0" />
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Right Panel - Preview/Edit */}
          <div className="flex-1 flex flex-col">
            {!selectedTemplate ? (
              <div className="flex-1 flex items-center justify-center text-gray-400">
                <div className="text-center">
                  <Eye className="w-12 h-12 mx-auto mb-3 opacity-40" />
                  <p className="text-sm">Wybierz szablon z listy</p>
                </div>
              </div>
            ) : previewLoading ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600"></div>
              </div>
            ) : preview ? (
              <>
                <div className="flex-1 overflow-y-auto p-5">
                  {/* Subject */}
                  <div className="mb-4">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Temat:</label>
                    {editMode ? (
                      <input
                        type="text"
                        value={editSubject}
                        onChange={e => setEditSubject(e.target.value)}
                        className="w-full px-3 py-2 border border-purple-300 rounded-lg text-sm font-medium focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    ) : (
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-2.5 text-sm font-medium text-gray-900">
                        {preview.subject}
                      </div>
                    )}
                  </div>

                  {/* Body */}
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Treść:</label>
                    {editMode ? (
                      <textarea
                        value={editBody}
                        onChange={e => setEditBody(e.target.value)}
                        rows={14}
                        className="w-full px-3 py-2 border border-purple-300 rounded-lg text-sm leading-relaxed focus:ring-2 focus:ring-purple-500 focus:border-transparent font-mono"
                      />
                    ) : (
                      <div
                        className="bg-white border border-gray-200 rounded-lg p-4 prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(preview.html) }}
                      />
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="border-t border-gray-200 p-3 bg-gray-50 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setEditMode(!editMode)}
                      className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors flex items-center gap-1.5 ${
                        editMode
                          ? 'bg-purple-100 text-purple-700'
                          : 'text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      <Pencil size={14} />
                      {editMode ? 'Podglad' : 'Edytuj'}
                    </button>
                    <button
                      onClick={() => {
                        const text = editMode
                          ? `Temat: ${editSubject}\n\n${editBody}`
                          : `Temat: ${preview.subject}\n\n${preview.body}`;
                        navigator.clipboard.writeText(text);
                      }}
                      className="px-3 py-2 text-gray-600 hover:bg-gray-200 rounded-lg text-xs font-medium flex items-center gap-1.5"
                    >
                      <FileText size={14} />
                      Kopiuj
                    </button>
                  </div>

                  <button
                    onClick={handleSendEmail}
                    disabled={sending}
                    className="px-5 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all shadow-md text-sm font-medium flex items-center gap-2 disabled:opacity-50"
                  >
                    <Send size={15} />
                    {sending ? 'Wysylanie...' : 'Wyślij email'}
                  </button>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-red-500">
                <div className="text-center">
                  <X className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p className="text-sm">Blad podgladu</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
